import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./FilterURL.css";

// ─── Config ───────────────────────────────────────────────────────────────────

const GROUPS = [
  { key: "cash", label: "Cash", icon: "fa-money-bill-wave", charges: false },
  { key: "pc", label: "PC", icon: "fa-gem", charges: true },
  {
    key: "lab",
    label: "Lab",
    icon: "fa-flask",
    charges: true,
    hasReport: true,
  },
  { key: "bar", label: "Bar", icon: "fa-bars-staggered", charges: true },
  { key: "coin", label: "Coin", icon: "fa-coins", charges: true },
  {
    key: "ginni",
    label: "Ginni",
    icon: "fa-circle-dot",
    charges: true,
    hasReport: true,
  },
  {
    key: "silver",
    label: "Silver",
    icon: "fa-circle-half-stroke",
    charges: true,
    hasBar: true,
  },
  {
    key: "rwg",
    label: "RWG",
    icon: "fa-circle-half-stroke",
    charges: false,
    hasBar: false,
  },
  {
    key: "rws",
    label: "RWS",
    icon: "fa-circle-half-stroke",
    charges: false,
    hasBar: false,
  },
];

const CHARGE_OPTS = [
  { v: "paid", label: "Paid", dotClass: "furl-dot-paid" },
  { v: "due", label: "Due", dotClass: "furl-dot-due" },
  { v: "payback", label: "Payable back", dotClass: "furl-dot-payback" },
];

// ─── URL helpers ──────────────────────────────────────────────────────────────
// Format: ?f=pc:due,lab_with:paid,lab_without,ginni,silver,silver_bar,cash

export function encodeFilters(filters) {
  const tokens = [];
  for (const [key, val] of Object.entries(filters)) {
    if (!val) continue;
    tokens.push(val === true ? key : `${key}:${val}`);
  }
  return tokens.join(",");
}

function decodeFilters(str) {
  const result = {};
  if (!str) return result;
  for (const token of str.split(",")) {
    if (!token.trim()) continue;
    const [key, charge] = token.split(":");
    result[key] = charge || true;
  }
  return result;
}

function readFromURL() {
  const params = new URLSearchParams(window.location.search);
  return decodeFilters(params.get("f") || "");
}

function writeToURL(filters) {
  const encoded = encodeFilters(filters);
  const url = new URL(window.location.href);
  if (encoded) url.searchParams.set("f", encoded);
  else url.searchParams.delete("f");
  window.history.replaceState({}, "", url.toString());
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function FilterURL({ onChange, isDailyReport }) {
  const [filters, setFilters] = useState(readFromURL);
  const [open, setOpen] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  useEffect(() => {
    writeToURL(filters);
    onChange?.(filters);
  }, [filters]);

  const setFilter = useCallback((key, val) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!val) delete next[key];
      else next[key] = val;
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);

  const toggleOpen = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const activeCount = Object.values(filters).filter(Boolean).length;
  const fStr = encodeFilters(filters);

  return (
    <>
      {/* ── Top bar ── */}
      <div className="dlr-bar">
        <h2 className="dlr-title">
          {isDailyReport ? "Daily" : "Customer"} Ledger Report
        </h2>
        <div className="dlr-bar-right">
          <button
            className={`dlr-filter-btn ${drawerOpen ? "active" : ""}`}
            onClick={() => setDrawerOpen((o) => !o)}
          >
            <i className="fas fa-sliders" style={{ fontSize: 13 }} />
            Filters
            {activeCount > 0 && <span className="dlr-fc">{activeCount}</span>}
          </button>
          {activeCount > 0 && (
            <button className="dlr-clear-btn" onClick={clearAll}>
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── URL preview bar ── */}
      {/* {fStr && (
        <div className="furl-url-bar">
          <span className="furl-url-label">?f=</span>
          <span className="furl-url-val">{fStr}</span>
        </div>
      )} */}

      {/* ── Drawer + backdrop via portal ── */}
      {createPortal(
        <>
          <div
            className={`fdr-backdrop${drawerOpen ? " fdr-backdrop-visible" : ""}`}
            onClick={() => setDrawerOpen(false)}
          />

          <div className={`fdr-drawer${drawerOpen ? " fdr-drawer-open" : ""}`}>
            {/* header */}
            <div className="fdr-header">
              <span className="fdr-header-title">
                <i
                  className="fas fa-sliders fdr-header-icon"
                  aria-hidden="true"
                />
                Filters
                {activeCount > 0 && (
                  <span className="fdr-active-badge">{activeCount}</span>
                )}
              </span>
              <div className="fdr-header-actions">
                {activeCount > 0 && (
                  <button className="fdr-clear-btn" onClick={clearAll}>
                    <i className="fas fa-xmark" aria-hidden="true" />
                    Clear all
                  </button>
                )}
                <button
                  className="fdr-close-btn"
                  onClick={() => setDrawerOpen(false)}
                  title="Close"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* scrollable body */}
            <div className="fdr-body">
              {GROUPS.map((g) => (
                <GroupSection
                  key={g.key}
                  group={g}
                  filters={filters}
                  setFilter={setFilter}
                  isOpen={open.has(g.key)}
                  onToggleOpen={() => toggleOpen(g.key)}
                />
              ))}
            </div>

            {/* footer */}
            <div
              className={`fdr-footer${activeCount > 0 ? " fdr-footer-active" : ""}`}
            >
              {activeCount > 0 ? (
                <>
                  <i
                    className="fas fa-circle-check fdr-footer-icon"
                    aria-hidden="true"
                  />
                  <span>
                    <strong>{activeCount}</strong> filter
                    {activeCount > 1 ? "s" : ""} active — results update live
                  </span>
                </>
              ) : (
                <>
                  <i
                    className="fas fa-info-circle fdr-footer-icon"
                    aria-hidden="true"
                  />
                  <span>Select filters above to narrow results</span>
                </>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

// ─── Group section ────────────────────────────────────────────────────────────

function GroupSection({ group: g, filters, setFilter, isOpen, onToggleOpen }) {
  const ownKeys = g.hasReport
    ? [g.key, `${g.key}_with`, `${g.key}_without`]
    : g.hasBar
      ? [g.key, `${g.key}_bar`]
      : [g.key];

  const selCount = ownKeys.filter((k) => filters[k]).length;

  return (
    <div className={`fp-section${isOpen ? " fp-section-open" : ""}`}>
      <button className="fp-section-hd" onClick={onToggleOpen}>
        <span className="fp-section-left">
          <i className={`fas ${g.icon} fp-section-icon`} aria-hidden="true" />
          <span className="fp-section-label">{g.label}</span>
          {selCount > 0 && <span className="fp-sel-badge">{selCount}</span>}
        </span>
        <i
          className={`fas fa-chevron-down fp-chevron${isOpen ? " fp-chevron-open" : ""}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="fp-section-body">
          {g.hasReport ? (
            <ReportGroup group={g} filters={filters} setFilter={setFilter} />
          ) : (
            <SimpleGroup group={g} filters={filters} setFilter={setFilter} />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Simple group (pc, bar, coin, silver, cash) ───────────────────────────────

function SimpleGroup({ group: g, filters, setFilter }) {
  const val = filters[g.key];
  const barVal = filters[`${g.key}_bar`];

  const togglePresence = () => {
    if (val) {
      setFilter(g.key, null);
      setFilter(`${g.key}_bar`, null);
    } else setFilter(g.key, true);
  };

  const pickCharge = (c) => {
    if (!val) setFilter(g.key, c);
    else if (val === c) setFilter(g.key, true);
    else setFilter(g.key, c);
  };

  return (
    <>
      <ToggleRow
        label={`Has ${g.label}`}
        active={!!val}
        onToggle={togglePresence}
      />
      {g.hasBar && (
        <ToggleRow
          label="Silver bar"
          active={!!barVal}
          onToggle={() => setFilter(`${g.key}_bar`, barVal ? null : true)}
          indent
        />
      )}
      {g.charges && val && (
        <ChargeChips
          current={typeof val === "string" ? val : null}
          onPick={pickCharge}
        />
      )}
    </>
  );
}

// ─── Report group (lab, ginni) ────────────────────────────────────────────────
// has / with-report / without-report are mutually exclusive slots

function ReportGroup({ group: g, filters, setFilter }) {
  const anyVal = filters[g.key];
  const withVal = filters[`${g.key}_with`];
  const woVal = filters[`${g.key}_without`];

  const clearGroup = () => {
    setFilter(g.key, null);
    setFilter(`${g.key}_with`, null);
    setFilter(`${g.key}_without`, null);
  };

  const pick = (slot) => {
    const already =
      (slot === "any" && anyVal) ||
      (slot === "with" && withVal) ||
      (slot === "without" && woVal);
    clearGroup();
    if (!already) {
      if (slot === "any") setFilter(g.key, true);
      if (slot === "with") setFilter(`${g.key}_with`, true);
      if (slot === "without") setFilter(`${g.key}_without`, true);
    }
  };

  const pickCharge = (slot, c) => {
    const key = slot === "any" ? g.key : `${g.key}_${slot}`;
    setFilter(key, filters[key] === c ? true : c);
  };

  return (
    <>
      {/* has (any) */}
      <ToggleRow
        label={`Has ${g.label} (any)`}
        active={!!anyVal}
        onToggle={() => pick("any")}
      />
      {anyVal && (
        <ChargeChips
          current={typeof anyVal === "string" ? anyVal : null}
          onPick={(c) => pickCharge("any", c)}
        />
      )}

      {/* with report */}
      <div className="fp-subgroup-label">
        <i className="fas fa-file-circle-check fp-sg-icon" aria-hidden="true" />
        With report
      </div>
      <ToggleRow
        label="Has report"
        active={!!withVal}
        onToggle={() => pick("with")}
        indent
      />
      {withVal && (
        <ChargeChips
          current={typeof withVal === "string" ? withVal : null}
          onPick={(c) => pickCharge("with", c)}
          indent
        />
      )}

      {/* without report */}
      <div className="fp-subgroup-label">
        <i className="fas fa-file-circle-xmark fp-sg-icon" aria-hidden="true" />
        Without report
      </div>
      <ToggleRow
        label="No report"
        active={!!woVal}
        onToggle={() => pick("without")}
        indent
      />
      {woVal && (
        <ChargeChips
          current={typeof woVal === "string" ? woVal : null}
          onPick={(c) => pickCharge("without", c)}
          indent
        />
      )}
    </>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function ToggleRow({ label, active, onToggle, indent = false }) {
  return (
    <label
      className={`furl-toggle-row${indent ? " furl-toggle-row-indent" : ""}`}
    >
      <span
        className={`furl-toggle${active ? " furl-toggle-on" : ""}`}
        onClick={onToggle}
        role="switch"
        aria-checked={active}
        tabIndex={0}
        onKeyDown={(e) => e.key === " " && onToggle()}
      >
        <span className="furl-toggle-thumb" />
      </span>
      <span className="furl-toggle-label">{label}</span>
    </label>
  );
}

function ChargeChips({ current, onPick, indent = false }) {
  return <></>;
  //   return (
  //     <div className={`furl-chips${indent ? " furl-chips-indent" : ""}`}>
  //       {CHARGE_OPTS.map((opt) => (
  //         <button
  //           key={opt.v}
  //           className={`furl-chip${current === opt.v ? ` furl-chip-${opt.v}` : ""}`}
  //           onClick={() => onPick(opt.v)}
  //         >
  //           <span className={`furl-dot ${opt.dotClass}`} />
  //           {opt.label}
  //         </button>
  //       ))}
  //     </div>
  //   );
}
